import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, ExternalLink } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { candidateService, type CandidateListResult } from '@core/supabase/services/candidateService';
import { applicationService } from '@core/supabase/services/applicationService';
import { candidateTagService } from '@core/supabase/services/candidateTagService';
import { orgService } from '@core/supabase/services/orgService';
import {
  type Application,
  type OrgUser,
} from '@core/types';
import { RecordHeader } from '@shared/components/ui/RecordHeader';
import { RecordDetailLayout } from '@shared/components/ui/RecordDetailLayout';
import { RightRailWithAssistant } from '@shared/components/ui/RightRailWithAssistant';
import { useTrackRecentlyViewed } from '@shared/hooks/useRecentlyViewed';
import { ListOperationsDropdown, type ListExportFmt } from '@shared/components/ui/ListOperationsDropdown';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import { CandidateDetailPageSkeleton } from './CandidateDetailPageSkeleton';
import { CandidateAiInterviewsPanel } from './CandidateAiInterviewsPanel';
import { CandidateQARail } from './CandidateQARail';
import { CandidateOverviewTab } from './tabs/CandidateOverviewTab';
import { CandidateEmailsTab } from './tabs/CandidateEmailsTab';
import { CandidateNotesTab } from './tabs/CandidateNotesTab';
import {
  defaultExportMeta,
  exportCandidatesCsv,
  exportCandidatesPdf,
  exportCandidatesXlsx,
} from './candidatesExportFormats';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';

type DetailTab = 'overview' | 'emails' | 'interviews' | 'notes';

const DETAIL_TABS: Array<{ id: DetailTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'emails', label: 'Emails' },
  { id: 'interviews', label: 'Interviews' },
  { id: 'notes', label: 'Notes' },
];

function shortId(id: string) {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

export function CandidateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = usePermissions();
  const [candidate, setCandidate] = useState<import('@core/types').Candidate | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [teamMembers, setTeamMembers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [appsLoading, setAppsLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [pageToast, setPageToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [tags, setTags] = useState<import('@core/supabase/services/candidateTagService').CandidateTag[]>([]);
  const _ref = useRef<HTMLInputElement>(null);

  // Tab state via URL param — instant switch
  const activeTab = (searchParams.get('tab') as DetailTab | null) ?? 'overview';

  const setActiveTab = useCallback(
    (t: DetailTab) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', t);
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  useTrackRecentlyViewed(
    useMemo(
      () =>
        candidate
          ? {
              kind: 'candidate' as const,
              id: candidate.id,
              label: candidate.full_name || candidate.email || 'Candidate',
              sublabel:
                [candidate.current_title, candidate.current_company].filter(Boolean).join(' · ') ||
                undefined,
              to: `/candidates/${candidate.id}`,
            }
          : null,
      [candidate?.id, candidate?.full_name, candidate?.email, candidate?.current_title, candidate?.current_company],
    ),
  );

  const pushPageToast = useCallback((kind: 'ok' | 'err', text: string) => {
    setPageToast({ kind, text });
  }, []);

  useEffect(() => {
    if (!pageToast) return;
    const t = window.setTimeout(() => setPageToast(null), 4200);
    return () => window.clearTimeout(t);
  }, [pageToast]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    candidateService
      .getById(id)
      .then((c) => { setCandidate(c); setLoadError(null); })
      .catch((e) => setLoadError(e instanceof Error ? e.message : 'Could not load candidate'))
      .finally(() => setLoading(false));
    candidateTagService.listByCandidate(id).then(setTags).catch(() => setTags([]));
  }, [id]);

  useEffect(() => {
    if (!user?.orgId) return;
    orgService.getTeamMembers(user.orgId).then(setTeamMembers).catch(() => setTeamMembers([]));
  }, [user?.orgId]);

  useEffect(() => {
    if (!user?.orgId || !id) return;
    setAppsLoading(true);
    applicationService
      .list(user.orgId, { candidateId: id, limit: 500 })
      .then((list) => setApplications(Array.isArray(list) ? list : []))
      .catch(() => setApplications([]))
      .finally(() => setAppsLoading(false));
  }, [user?.orgId, id]);

  // Poll for async AI work status changes
  useEffect(() => {
    if (!id) return;
    const st = candidate?.async_ai_work_status;
    if (st !== 'queued' && st !== 'processing') return;
    const t = window.setInterval(() => {
      candidateService.getById(id).then((c) => { if (c) setCandidate(c); });
    }, 3500);
    return () => window.clearInterval(t);
  }, [id, candidate?.async_ai_work_status]);

  const createdByLabel = useMemo(() => {
    if (!candidate) return '—';
    const m = teamMembers.find(
      (u) => u.keycloak_sub === candidate.created_by || u.id === candidate.created_by
    );
    return m?.display_name?.trim() || m?.email || shortId(candidate.created_by);
  }, [teamMembers, candidate]);

  const runExportFormat = useCallback(
    async (fmt: ListExportFmt, scope: 'selection' | 'all') => {
      if (!user?.orgId || !id) return;
      try {
        let rows: import('@core/types').Candidate[] = [];
        if (scope === 'selection') {
          const c = await candidateService.getById(id);
          if (!c || c.org_id !== user.orgId) { pushPageToast('err', 'Could not load this candidate for export.'); return; }
          rows = [c];
        } else {
          const pageSize = 500;
          const first = (await candidateService.list(user.orgId, { page: 1, pageSize, sortBy: 'created_at', sortAsc: false })) as CandidateListResult;
          rows = [...first.data];
          let page = 2;
          while (rows.length < first.total) {
            const next = (await candidateService.list(user.orgId, { page, pageSize, sortBy: 'created_at', sortAsc: false })) as CandidateListResult;
            rows.push(...next.data);
            if (next.data.length === 0) break;
            page += 1;
          }
        }
        if (rows.length === 0) { pushPageToast('err', scope === 'selection' ? 'Could not export this candidate.' : 'No candidates to export.'); return; }
        const dayStamp = new Date().toISOString().slice(0, 10);
        const nameSlug = (rows[0]?.full_name ?? 'record').replace(/[^\w.-]+/g, '_').replace(/_+/g, '_').slice(0, 48);
        const base = scope === 'selection' ? `candidate_${nameSlug || 'record'}_${dayStamp}` : `candidates_${dayStamp}`;
        const meta = defaultExportMeta({
          recordCount: rows.length,
          reportTitle: scope === 'selection' ? `Candidate — ${rows[0]?.full_name ?? id}` : 'Candidates',
          subtitle: scope === 'selection' ? 'Single candidate — full record' : 'All active candidates in organization',
          generatedAt: new Date(),
        });
        if (fmt === 'csv') exportCandidatesCsv(rows, `${base}.csv`);
        else if (fmt === 'xlsx') await exportCandidatesXlsx(rows, `${base}.xlsx`, meta);
        else await exportCandidatesPdf(rows, `${base}.pdf`, meta);
        pushPageToast('ok', 'Export started — check your downloads.');
      } catch (e) {
        pushPageToast('err', e instanceof Error ? e.message : 'Export failed.');
      }
    },
    [user?.orgId, id, pushPageToast]
  );

  const handleDelete = async () => {
    if (!id) return;
    try {
      await candidateService.softDelete(id);
      pushPageToast('ok', 'Candidate removed from pool.');
      setShowDelete(false);
      navigate('/candidates');
    } catch (e) {
      pushPageToast('err', e instanceof Error ? e.message : 'Remove failed.');
    }
  };

  // ── RENDER ───────────────────────────────────────────────────────────────

  if (!user?.orgId) return null;

  if (loading) {
    return (
      <div className="w-full min-w-0">
        <RecordHeader
          title="Loading…"
          breadcrumb={[{ label: 'Candidates', href: '/candidates' }, { label: 'Loading…' }]}
        />
        <CandidateDetailPageSkeleton />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="w-full min-w-0 space-y-4">
        <RecordHeader
          title="Could not load candidate"
          meta={loadError}
          breadcrumb={[{ label: 'Candidates', href: '/candidates' }, { label: 'Error' }]}
        />
        <button
          type="button"
          className="btn-secondary text-sm px-4 py-2"
          onClick={() => {
            if (!id) return;
            setLoading(true);
            setLoadError(null);
            candidateService
              .getById(id)
              .then((c) => { setCandidate(c); setLoadError(null); })
              .catch((e) => setLoadError(e instanceof Error ? e.message : 'Could not load candidate'))
              .finally(() => setLoading(false));
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!candidate) {
    return <div className="vscode-text-secondary p-4">Candidate not found.</div>;
  }

  const linkedinHref = (() => {
    const li = candidate.linkedin_url?.trim();
    if (!li) return null;
    return li.startsWith('http') ? li : `https://${li}`;
  })();

  void createdByLabel; // referenced in export meta
  void tags; // retained; may power a tags display in future
  void _ref;

  return (
    <div className="w-full min-w-0">
      {pageToast ? (
        <div
          className={`fixed bottom-4 right-4 z-[80] max-w-sm rounded-lg border px-4 py-3 text-sm shadow-lg ${
            pageToast.kind === 'ok'
              ? 'border-emerald-500/40 bg-[var(--vscode-bg-elevated)] vscode-text-primary'
              : 'border-red-500/40 bg-[var(--vscode-bg-elevated)] text-red-400'
          }`}
          role="status"
        >
          {pageToast.text}
        </div>
      ) : null}

      <RecordDetailLayout
        variant="two-thirds"
        header={
          <RecordHeader
            title={candidate.full_name}
            meta={[candidate.current_title, candidate.current_company].filter(Boolean).join(' @ ') || candidate.email || undefined}
            breadcrumb={[
              { label: 'Candidates', href: '/candidates' },
              { label: candidate.full_name },
            ]}
            actionsEnd={
              <div className="flex items-center gap-2">
                {linkedinHref ? (
                  <a
                    href={linkedinHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--vscode-border-primary)] px-3 py-1.5 text-[12px] font-medium vscode-text-secondary transition-colors hover:bg-[var(--vscode-bg-hover)]"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> View LinkedIn
                  </a>
                ) : null}
                <ListOperationsDropdown
                  objectHistoryHref={objectHistoryUrl('candidates')}
                  onExport={(fmt, scope) => void runExportFormat(fmt, scope)}
                />
                {perms.canDeleteCandidate ? (
                  <button
                    type="button"
                    className="rounded-lg border border-red-300 px-3 py-1.5 text-[12px] font-medium text-red-500 transition-colors hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                    onClick={() => setShowDelete(true)}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            }
          />
        }
        main={
          <div className="min-w-0">
            {/* Sticky inner tab strip — 44 px, emerald 2px underline on active */}
            <div
              className="sticky top-0 z-10 flex items-center border-b border-[var(--vscode-border-primary)] bg-[var(--vscode-bg)] px-4"
              style={{ height: 44 }}
              role="tablist"
              aria-label="Candidate sections"
            >
              {DETAIL_TABS.map((t) => {
                const active = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveTab(t.id)}
                    className={[
                      'relative mr-1 px-3 py-2 transition-colors',
                      active
                        ? 'text-[13px] font-medium vscode-text-primary'
                        : 'text-[11px] font-medium uppercase tracking-wide vscode-text-tertiary hover:vscode-text-secondary',
                    ].join(' ')}
                  >
                    {t.label}
                    {active ? (
                      <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-t-full bg-emerald-500" aria-hidden />
                    ) : null}
                  </button>
                );
              })}
            </div>

            {/* Tab panels */}
            <div className="px-4 pt-6" role="tabpanel">
              {activeTab === 'overview' ? (
                <CandidateOverviewTab candidate={candidate} applications={applications} />
              ) : null}
              {activeTab === 'emails' ? (
                <CandidateEmailsTab applications={applications} />
              ) : null}
              {activeTab === 'interviews' ? (
                appsLoading ? (
                  <div className="flex items-center gap-2 py-4 text-[13px] vscode-text-tertiary">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading interviews…
                  </div>
                ) : (
                  <CandidateAiInterviewsPanel
                    candidateId={candidate.id}
                    candidateName={candidate.full_name}
                    candidateEmail={candidate.email}
                    applications={applications}
                    onOpenApplicationsTab={() => navigate(`/applications?candidateId=${candidate.id}`)}
                  />
                )
              ) : null}
              {activeTab === 'notes' ? (
                <CandidateNotesTab candidateId={candidate.id} />
              ) : null}
            </div>
          </div>
        }
        metaRail={
          <RightRailWithAssistant
            recordId={candidate.id}
            entityKind="candidate"
            details={
              <CandidateQARail
                candidateId={candidate.id}
                candidateName={candidate.full_name}
              />
            }
          />
        }
      />

      <ConfirmDialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        title="Remove Candidate"
        message={`Remove ${candidate.full_name} from your candidate pool? This action can be undone by contacting support.`}
        confirmLabel="Remove"
      />
    </div>
  );
}
